import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAppCookies } from '@app/providers/AppCookieProvider';
import { UserStore } from '@stores/UserStore';
import { UserProfileViewCreator, useUserProfileViewCreator } from '@viewCreators/UserProfileViewCreator';
import { isMissingName } from './nameGuardUtils';

export const useNameGuardController = () => {
  const cookies = useAppCookies();
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const userId = cookies.profile?.id ? Number(cookies.profile.id) : 0;
  const { data: user, isLoading, isError } = useUserProfileViewCreator(userId);

  // Don't lock anyone out if the user lookup fails; only block once we know the name is missing.
  const nameRequired = !isLoading && !isError && !!user && isMissingName(user.name);

  const handleNameChange = useCallback((value: string) => {
    setName(value);
  }, []);

  const handleSaveName = useCallback(async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setSaving(true);
    setSaveError('');
    try {
      const response = await new UserStore().updateMyProfile({ name: trimmed });
      if (response.status !== 'SUCCESS' || response.statusCode >= 400) {
        const msg =
          (response.result as any)?.message || response.statusMessage || 'Something went wrong. Please try again.';
        setSaveError(msg);
        return;
      }
      await queryClient.invalidateQueries({ queryKey: [UserProfileViewCreator.name] });
    } catch {
      setSaveError('Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [name, queryClient]);

  return {
    loading: isLoading,
    nameRequired,
    name,
    saving,
    saveError,
    handleNameChange,
    handleSaveName,
  };
};
